import { isOrderEditable } from '@cutr/constants/windows/windows';
import { getOrderPricingSummary } from '@cutr/constants/windows/windows-pricing';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { Button, RouterButton } from '@/primitives/Button';
import { Cart, Icon, Plus } from '@/primitives/Icons';
import { currencyFormatter } from '@/utils/format';

import { useWindowLoaderData, useWindowStore } from '../api/windowStore';
import { OrderTitle } from '../blocks/OrderTitle';
import { ConfigurationsProvider } from '../interfaces/configurations/ConfigurationsContext';
import { OrderConfiguration } from '../interfaces/configurations/OrderConfiguration';
import { getPositionsQuery, useNewPosition } from '../queries/position/crud';
import styles from './ResellerOrderConfiguration.module.css';

export const ResellerOrderConfiguration = () => {
  const { t } = useTranslation();
  const { orderId, positionId } = useParams();
  const order = useWindowLoaderData();
  const { onNewPosition } = useNewPosition();

  const { totalAmount, requiresManualQuoting } = getOrderPricingSummary(order);

  const reviewButtonLabel = requiresManualQuoting
    ? t('reseller.cta.reviewOrderRequiresManualQuoting', {
        totalAmount: currencyFormatter(totalAmount / 100, 'eur'),
      })
    : t('reseller.cta.reviewOrder', {
        totalAmount: currencyFormatter(totalAmount / 100, 'eur'),
      });

  const isEditable = isOrderEditable(order.status);

  return (
    <main className={styles.content}>
      <OrderTitle readonly={!isEditable} />
      <div className="opposites" style={{ paddingBottom: 'var(--space-m)' }}>
        <PositionTabNav />
        <div className="flexAlign">
          {isEditable && (
            <Button
              name="add-position"
              icon={<Icon icon={<Plus />} style={{ minWidth: '1rem' }} />}
              onClick={onNewPosition}
              style={{
                fontSize: '1rem',
                fontWeight: '600',
                backgroundColor: 'var(--gray-1)',
              }}
              variant="secondary"
            >
              {t('reseller.orderConfig.addPosition')}
            </Button>
          )}
          <RouterButton
            to={`/windows/orders/${orderId}/review`}
            icon={<Icon icon={<Cart />} style={{ minWidth: '1rem' }} />}
            style={{
              width: 'auto',
            }}
          >
            {reviewButtonLabel}
          </RouterButton>
        </div>
      </div>

      {!positionId ? <OrderLevelConfiguration /> : <Outlet />}
    </main>
  );
};

const OrderLevelConfiguration = () => {
  const { configurations, setConfigurations } = useWindowStore();
  const order = useWindowLoaderData();

  const isEditable = isOrderEditable(order.status);

  return (
    <ConfigurationsProvider
      configurations={configurations}
      onConfigurationChange={setConfigurations}
    >
      <OrderConfiguration readonly={!isEditable} />
    </ConfigurationsProvider>
  );
};

export const PositionTabNav = () => {
  const { t } = useTranslation();
  const { orderId, positionId } = useParams();
  const navigate = useNavigate();
  const { data: positions } = useQuery(getPositionsQuery(orderId!));

  const isOrderConfigTab = !positionId;

  return (
    <section className={styles.tabNav}>
      <Button
        role="tab"
        aria-selected={isOrderConfigTab}
        variant="tab"
        onClick={() => navigate(`/windows/orders/${orderId}/configuration`)}
        className={isOrderConfigTab ? styles.activeTab : styles.tab}
      >
        {t('reseller.orderConfig.navLabel')}
      </Button>
      {positions?.map((p, index) => (
        <Button
          key={`tab-${p.id}`}
          role="tab"
          aria-selected={positionId === p.id}
          data-testid="tabButton"
          variant="tab"
          onClick={() =>
            navigate(`/windows/orders/${orderId}/configuration/${p.id}`)
          }
          className={positionId === p.id ? styles.activeTab : styles.tab}
        >
          {`${index + 1}. `}
          {p.title}
        </Button>
      ))}
    </section>
  );
};
